import { CfTokenRejectionReason, CfTokenResult, isCfTokenRejectionReason } from '../api/cloudflareTokenApi';

export type CfTokenCheckState =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'unchecked' }
  | { phase: 'answered'; result: CfTokenResult; coversOtherZones: boolean };

export type ConnectProblem = CfTokenRejectionReason | 'beta_closed' | 'flow_not_taking_token' | 'request_failed';

export type CfTokenFieldStatus = {
  kind: 'idle' | 'checking' | 'success' | 'warning' | 'error';
  message?: string;
  blocking: boolean;
  offersRetry: boolean;
};

const BREADTH_WARNING =
  'This token has access to multiple domains. We recommend limiting it to one domain, but you can still continue.';

const rejectionCopy = (reason: CfTokenRejectionReason, domain: string): string => {
  switch (reason) {
    case 'missing_permissions':
      return 'This token is missing some required permissions. Create a new token using the link above. All permissions are pre-filled.';
    case 'invalid':
      return "This token doesn't appear to be valid. Confirm you copied the full token value from Cloudflare and try again.";
    case 'zone_not_covered':
      return `We can't find this domain in your Cloudflare account. Make sure you've added ${domain} to Cloudflare and that the token is for the same account, then try again.`;
    case 'unavailable':
      return 'Cloudflare took too long to respond. Your token may still be valid, so please';
  }
};

const rejected = (reason: CfTokenRejectionReason, domain: string): CfTokenFieldStatus => ({
  kind: 'error',
  message: rejectionCopy(reason, domain),
  blocking: true,
  offersRetry: reason === 'unavailable',
});

const NOT_BLOCKING = { blocking: false, offersRetry: false } as const;

const rejectionReasonOnScreen = (
  check: CfTokenCheckState,
  connectProblem: ConnectProblem | null
): CfTokenRejectionReason | null => {
  if (isCfTokenRejectionReason(connectProblem)) return connectProblem;
  if (check.phase === 'answered' && check.result !== 'valid') return check.result;

  return null;
};

export const errorShownOnTokenField = (
  check: CfTokenCheckState,
  connectProblem: ConnectProblem | null
): Exclude<CfTokenRejectionReason, 'unavailable'> | null => {
  const reason = rejectionReasonOnScreen(check, connectProblem);

  return reason === 'unavailable' ? null : reason;
};

export const deriveCfTokenFieldStatus = (
  check: CfTokenCheckState,
  connectProblem: ConnectProblem | null,
  domain: string
): CfTokenFieldStatus => {
  if (isCfTokenRejectionReason(connectProblem)) return rejected(connectProblem, domain);

  if (check.phase === 'idle' || check.phase === 'unchecked') return { kind: 'idle', ...NOT_BLOCKING };
  if (check.phase === 'checking') return { kind: 'checking', ...NOT_BLOCKING };
  if (check.result !== 'valid') return rejected(check.result, domain);
  if (check.coversOtherZones) return { kind: 'warning', message: BREADTH_WARNING, ...NOT_BLOCKING };

  return { kind: 'success', ...NOT_BLOCKING };
};

/**
 * The field text Nexus is told the customer saw. A retry-offering message ends
 * mid-sentence on its button, and a warning does not stop the customer.
 */
export const nexusErrorTextOf = ({ kind, message, offersRetry }: CfTokenFieldStatus): string | null =>
  kind === 'error' && !offersRetry ? (message ?? null) : null;
